import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useOwnerLayoutData } from '@/hooks/useOwnerLayoutData';
import { APP_CONFIG } from '@/config/app.config';
import { cn } from '@/lib/utils';
import ApperIcon from '@/components/ApperIcon';
import UserMenu from '@/components/UserMenu';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import CanvasProvider, { useCanvases } from '@/components/canvas/CanvasProvider';
import SaveIndicator from '@/components/canvas/SaveIndicator';
import { ProgressBar, statusKey } from '@/components/canvas/bits';

const ARCS = [
  { name: 'Make the case', dot: 'bg-(color:--step-1)', from: 0, to: 4 },
  { name: 'Make it buildable', dot: 'bg-(color:--arc-build)', from: 5, to: 6 },
  { name: 'Make it happen', dot: 'bg-(color:--step-3)', from: 7, to: 9 },
];

const STATUS_ICON = {
  Confirmed: { name: 'CircleCheck', cls: 'text-(color:--step-3)' },
  'In progress': { name: 'CirclePlay', cls: 'text-(color:--step-5)' },
  Review: { name: 'TriangleAlert', cls: 'text-highlight' },
  'Not started': { name: 'Circle', cls: 'opacity-40' },
};

function Chain({ onNavigate }) {
  const { states, loading, defs } = useCanvases();
  const confirmed = Object.values(states).filter((s) => s.confirmed).length;
  return (
    <div className="space-y-4 px-3 pb-6">
      <div className="space-y-2 px-2">
        <div className="flex items-baseline justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70">Your canvases</p>
          <p className="text-xs font-medium tabular-nums">{loading ? '' : `${confirmed} of ${defs.length} confirmed`}</p>
        </div>
        <ProgressBar value={(100 * confirmed) / defs.length} className="bg-sidebar-border" barClassName="bg-highlight" label="Canvases confirmed" />
      </div>
      {loading ? (
        <div className="space-y-2 px-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full bg-sidebar-accent" />
          ))}
        </div>
      ) : (
        ARCS.map((arc) => (
          <div key={arc.name} className="space-y-1">
            <p className="flex items-center gap-2 px-2 text-[11px] font-semibold uppercase tracking-wider opacity-70">
              <span className={cn('size-2 rounded-full', arc.dot)} />
              {arc.name}
            </p>
            <ul className="space-y-0.5">
              {defs
                .filter((d) => d.no >= arc.from && d.no <= arc.to)
                .map((d) => {
                  const key = statusKey(states[d.no]);
                  const icon = STATUS_ICON[key];
                  return (
                    <li key={d.no}>
                      <NavLink
                        to={`/canvas/${d.no}`}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
                            isActive ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent/60',
                          )
                        }
                      >
                        <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold text-white', arc.dot)}>
                          {d.no === 0 ? <ApperIcon name="IdCard" size={13} /> : d.no}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{d.no === 0 ? 'Case Card' : d.short || d.title}</span>
                        <ApperIcon name={icon.name} size={16} className={cn('shrink-0', icon.cls)} aria-label={key} />
                      </NavLink>
                    </li>
                  );
                })}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

function Shell() {
  const { user, logout, homeRoute, navItems, initials, displayName } = useOwnerLayoutData();
  const { enabled } = useCanvases();
  const [navOpen, setNavOpen] = useState(false);
  const close = () => setNavOpen(false);

  return (
    <div className="min-h-svh bg-background text-foreground lg:flex">
      {navOpen && <div className="fixed inset-0 z-(--z-dropdown) bg-black/50 lg:hidden" onClick={close} aria-hidden="true" />}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-(--z-modal) flex w-(--spacing-sidebar) flex-col overflow-y-auto bg-sidebar text-sidebar-foreground transition-transform duration-(--transition-normal)',
          'lg:sticky lg:top-0 lg:z-auto lg:h-svh lg:shrink-0 lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        <Link
          to={homeRoute}
          onClick={close}
          className="flex h-(--spacing-header) shrink-0 items-center gap-3 border-b border-sidebar-border px-4 outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/60"
        >
          <span className="flex size-9 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground">
            <ApperIcon name={APP_CONFIG.icon} size={20} />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-heading text-base font-semibold leading-tight">{APP_CONFIG.name}</span>
            <span className="block truncate text-[11px] opacity-70">AI-Powered CX for executives</span>
          </span>
        </Link>
        <nav className="space-y-0.5 p-3" aria-label="Pages">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={close}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                  isActive ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent/60',
                )
              }
            >
              <ApperIcon name={item.icon ?? 'Circle'} size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        {enabled && <Chain onNavigate={close} />}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-(--z-sticky) flex h-(--spacing-header) shrink-0 items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur md:px-6">
          <button
            type="button"
            className="-ml-1 flex size-9 items-center justify-center rounded-lg hover:bg-accent lg:hidden"
            aria-label="Open navigation"
            onClick={() => setNavOpen(true)}
          >
            <ApperIcon name="Menu" size={22} />
          </button>
          <span className="font-heading text-base font-semibold lg:hidden">{APP_CONFIG.name}</span>
          <div className="ml-auto flex items-center gap-3">
            {enabled && <SaveIndicator className="hidden sm:flex" />}
            <UserMenu user={user} initials={initials} displayName={displayName} logout={logout} />
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 md:p-6 lg:p-8">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

export default function OwnerLayout() {
  return (
    <CanvasProvider>
      <Shell />
    </CanvasProvider>
  );
}
